import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  IconButton
} from '@mui/material';
import { Add, Delete, CheckCircle } from '@mui/icons-material';
import milestoneService from '../../services/milestoneService';

/**
 * CostCalculator
 * Interactive cost input with AI validation
 */
const CostCalculator = ({ milestoneName, location, onCostSubmit }) => {
  const [costType, setCostType] = useState('single'); // single, breakdown, itemized
  const [laborCost, setLaborCost] = useState('');
  const [workers, setWorkers] = useState('');
  const [days, setDays] = useState('');
  const [materials, setMaterials] = useState([{ item: '', cost: '' }]);
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [error, setError] = useState(null);

  const handleAddMaterial = () => {
    setMaterials([...materials, { item: '', cost: '' }]);
  };

  const handleRemoveMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const handleMaterialChange = (index, field, value) => {
    const newMaterials = [...materials];
    newMaterials[index][field] = value;
    setMaterials(newMaterials);
  };

  const calculateTotal = () => {
    const labor = parseFloat(laborCost) || 0;
    const materialsTotal = materials.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0);
    return labor + materialsTotal;
  };

  const handleValidate = async () => {
    if (!laborCost) {
      setError('Please enter labor cost');
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      const response = await milestoneService.validateCosts({
        milestoneName,
        laborCost: parseFloat(laborCost),
        laborDetails: {
          workers: parseInt(workers) || 1,
          days: parseInt(days) || 1
        },
        materials: materials.filter(m => m.item && m.cost),
        location: location || 'Zimbabwe'
      });

      if (response.success) {
        setValidation(response.data.validation);
      }
    } catch (err) {
      setError('Failed to validate costs');
      console.error(err);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = () => {
    if (!laborCost) {
      setError('Please enter labor cost');
      return;
    }

    const costData = {
      laborCost: parseFloat(laborCost),
      materialsCost: materials.reduce((sum, m) => sum + (parseFloat(m.cost) || 0), 0),
      breakdown: {
        type: costType,
        materials: materials.filter(m => m.item && m.cost)
      },
      laborDetails: {
        workers: parseInt(workers) || 1,
        days: parseInt(days) || 1
      },
      validation
    };

    onCostSubmit(costData);
  };

  return (
    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        💰 Cost Structure
      </Typography>

      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">How would you like to price this?</FormLabel>
        <RadioGroup value={costType} onChange={(e) => setCostType(e.target.value)}>
          <FormControlLabel value="single" control={<Radio />} label="Single labor cost for entire milestone" />
          <FormControlLabel value="breakdown" control={<Radio />} label="Labor + Materials breakdown" />
        </RadioGroup>
      </FormControl>

      {/* Labor Cost */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Labor Cost
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Total Labor Cost ($)"
              type="number"
              value={laborCost}
              onChange={(e) => setLaborCost(e.target.value)}
              required
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Number of Workers"
              type="number"
              value={workers}
              onChange={(e) => setWorkers(e.target.value)}
              helperText="For rate calculation"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Number of Days"
              type="number"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              helperText="For rate calculation"
            />
          </Grid>
        </Grid>

        {workers && days && laborCost && (
          <Alert severity="info" sx={{ mt: 1 }}>
            Rate per worker per day: ${(parseFloat(laborCost) / (parseInt(workers) * parseInt(days))).toFixed(2)}
          </Alert>
        )}
      </Box>

      {/* Materials (if breakdown selected) */}
      {costType === 'breakdown' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Materials & Equipment Hire
          </Typography>
          {materials.map((material, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Item"
                  value={material.item}
                  onChange={(e) => handleMaterialChange(index, 'item', e.target.value)}
                  placeholder="e.g., Cement, Equipment hire"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Cost ($)"
                  type="number"
                  value={material.cost}
                  onChange={(e) => handleMaterialChange(index, 'cost', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton onClick={() => handleRemoveMaterial(index)} color="error">
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button startIcon={<Add />} onClick={handleAddMaterial} size="small">
            Add Material
          </Button>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Total */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
        <Typography variant="h6">
          Total Cost: ${calculateTotal().toFixed(2)}
        </Typography>
      </Box>

      {/* Validation */}
      {validation && (
        <Alert severity={validation.isReasonable ? 'success' : 'warning'} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {validation.marketComparison}
          </Typography>
          {validation.suggestions && validation.suggestions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {validation.suggestions.map((suggestion, i) => (
                <Typography key={i} variant="caption" display="block">
                  • {suggestion}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          onClick={handleValidate}
          disabled={isValidating || !laborCost}
          startIcon={isValidating && <CircularProgress size={16} />}
        >
          {isValidating ? 'Validating...' : 'Validate Costs'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!laborCost}
          startIcon={<CheckCircle />}
        >
          Confirm Costs
        </Button>
      </Box>
    </Paper>
  );
};

export default CostCalculator;
